/**
 * ช่องติ๊ก "ไม่หักเงิน" + แนบรูปหลักฐาน — ใช้ร่วมกันระหว่างหน้าอนุมัติรับรองเวลา
 * กับฟอร์มลงเวลาย้อนหลัง. HR ใช้เมื่อพนักงานมาทำงานจริงแต่เข้าระบบไม่ได้.
 * state อยู่ใน useWaiverEvidence ส่วนการอัปโหลดเกิดตอนกดยืนยันเท่านั้น (เปลี่ยนใจไม่ทิ้งไฟล์ค้าง)
 */
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Paperclip, X } from 'lucide-react';
import { uploadFile } from '../../lib/api-client';
import { useToast } from '../ui/toast';

const MAX_MB = 5;
const MAX_BYTES = MAX_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'pdf']);
const EXTENSION_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'application/pdf': 'pdf',
};
const ACCEPT = 'image/*,.heic,.heif,application/pdf';

/** รอบที่กดไม่หักเงินได้ — ตรงกับ WAIVABLE_KINDS ฝั่ง worker */
export function isWaivableKind(kind: string): boolean {
  return kind === 'morning_in' || kind === 'evening_out';
}

/**
 * worker ตั้งนามสกุลไฟล์จากชื่อที่ส่งไป แล้วตรวจนามสกุลอีกครั้งตอนกดไม่หักเงิน.
 * ตัวเลือกไฟล์บน Android บางรุ่นส่งชื่อมาไม่มีนามสกุล — เติมจากชนิดไฟล์ให้ ไม่งั้นโดนปฏิเสธ
 */
function evidenceFileName(file: File): string | null {
  const name = file.name.trim();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : '';
  if (ALLOWED_EXTENSIONS.has(ext)) return name;
  const extFromMime = EXTENSION_BY_MIME[file.type];
  return extFromMime ? `${name || 'evidence'}.${extFromMime}` : null;
}

function fileProblem(file: File): string | null {
  if (!evidenceFileName(file)) return 'รองรับเฉพาะไฟล์ JPG, PNG, WEBP, HEIC, HEIF หรือ PDF';
  if (file.size === 0) return 'ไฟล์นี้ว่างเปล่า กรุณาเลือกไฟล์ใหม่';
  if (file.size > MAX_BYTES) {
    return `ไฟล์ใหญ่เกิน ${MAX_MB} MB (ไฟล์นี้ ${(file.size / 1024 / 1024).toFixed(1)} MB) กรุณาเลือกไฟล์ที่เล็กกว่านี้`;
  }
  return null;
}

export function useWaiverEvidence() {
  const [waive, setWaiveState] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  // ผูกผลอัปโหลดกับไฟล์ที่เลือก — บันทึกล้มแล้วกดซ้ำไม่ต้องอัปโหลดใหม่ แต่เปลี่ยนไฟล์แล้วต้องอัปโหลดไฟล์ใหม่เสมอ
  const uploaded = useRef<{ file: File; path: string } | null>(null);

  const setWaive = useCallback((next: boolean) => {
    setWaiveState(next);
    if (!next) setFile(null);
  }, []);
  const reset = useCallback(() => setWaive(false), [setWaive]);

  /** อัปโหลดถ้ายังไม่ได้อัปโหลด แล้วคืน field ที่ใส่ใน request ได้เลย — ไม่ได้ติ๊กจะคืน object ว่าง */
  async function requestFields(): Promise<{ waive_deduction?: boolean; evidence_path?: string }> {
    if (!waive) return {};
    // ติ๊กแล้วแต่ไม่มีไฟล์ ห้ามคืน object ว่าง — จะกลายเป็นอนุมัติธรรมดาโดยที่ HR ไม่รู้ตัว
    if (!file) throw new Error('แนบรูปหลักฐานก่อนกดไม่หักเงิน');
    if (uploaded.current?.file === file) return { waive_deduction: true, evidence_path: uploaded.current.path };
    setUploading(true);
    try {
      const path = await uploadFile(file, 'evidence', evidenceFileName(file) ?? file.name);
      uploaded.current = { file, path };
      return { waive_deduction: true, evidence_path: path };
    } finally {
      setUploading(false);
    }
  }

  return { waive, file, uploading, missingFile: waive && !file, setWaive, setFile, reset, requestFields };
}

/** Blob URL สำหรับรูปตัวอย่าง — คืนหน่วยความจำเมื่อเปลี่ยนไฟล์หรือออกจากหน้า */
function useImagePreviewUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url;
}

interface WaiverEvidenceFieldProps {
  waiver: ReturnType<typeof useWaiverEvidence>;
  disabled?: boolean;
  hint?: string;
}

export function WaiverEvidenceField({ waiver, disabled = false, hint }: WaiverEvidenceFieldProps) {
  const toast = useToast();
  const { waive, file, setWaive, setFile } = waiver;
  const previewUrl = useImagePreviewUrl(file);
  // HEIC แสดงผลไม่ได้ในหลาย browser — ถ้าโหลดรูปไม่ขึ้นให้เหลือแค่ชื่อไฟล์
  const [brokenPreviewUrl, setBrokenPreviewUrl] = useState<string | null>(null);

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    e.target.value = ''; // เลือกไฟล์เดิมซ้ำได้ (เช่น หลังกดเอาออก)
    if (!picked) return;
    const problem = fileProblem(picked);
    if (problem) {
      toast.error(problem);
      return;
    }
    setFile(picked);
  }

  return (
    <div className="space-y-2">
      <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
        <input
          type="checkbox"
          checked={waive}
          disabled={disabled}
          onChange={(e) => setWaive(e.target.checked)}
          className="mt-0.5 h-4 w-4 flex-shrink-0"
        />
        <span>ไม่หักเงิน (พนักงานมาทำงานจริง แต่เข้าระบบไม่ได้)</span>
      </label>

      {waive && (
        <div className="space-y-2 pl-6">
          {file && (
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              {previewUrl && previewUrl !== brokenPreviewUrl ? (
                <img
                  src={previewUrl}
                  alt="รูปหลักฐานที่เลือก"
                  onError={() => setBrokenPreviewUrl(previewUrl)}
                  className="w-12 h-12 object-cover rounded border border-gray-200 flex-shrink-0"
                />
              ) : (
                <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
              <p className="text-xs text-gray-700 truncate min-w-0 flex-1">{file.name}</p>
              <button
                type="button"
                onClick={() => setFile(null)}
                disabled={disabled}
                aria-label="เอาไฟล์ออก"
                className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <label
            className={`btn-secondary btn-slim inline-flex items-center cursor-pointer w-fit focus-within:ring-2 focus-within:ring-brand-600 ${
              disabled ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            <Paperclip className="w-3.5 h-3.5" /> {file ? 'เปลี่ยนไฟล์' : 'แนบรูปหลักฐาน'}
            <input type="file" accept={ACCEPT} disabled={disabled} onChange={onPick} className="sr-only" />
          </label>
          <p className="text-xs text-gray-500">รูปหรือ PDF ขนาดไม่เกิน {MAX_MB} MB</p>
        </div>
      )}

      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}
